import Panzoom from '@panzoom/panzoom'
import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { scrollIntoView } from 'seamless-scroll-polyfill'
import styled from 'styled-components'
import { DateTime, TimeOnly } from '@sevenrooms/timepiece'
import { HTMLContent } from '@sevenrooms/ui-kit/core'
import { BasicButton, IconButton } from '@sevenrooms/ui-kit/form'
import { Icon } from '@sevenrooms/ui-kit/icons'
import { Box, Flex, HStack, Root, DividerLine } from '@sevenrooms/ui-kit/layout'
import { gxTheme, type ThemeColor } from '@sevenrooms/ui-kit/theme'
import { Text } from '@sevenrooms/ui-kit/typography'
import { selectInventoryItems } from '../actions/navigation'
import EventDetails from '../components/EventDetails'
import ContinueButton from '../components/InventoryList/ContinueButton'
import { isMultiInventoryEnabled } from '../selectors/eventSelector'
import { calcTotalFeePrice } from '../utils/calcCharges'
import { hexToRgb } from '../utils/color'
import { adaptPhotoData, type RawPhotoData } from '../utils/photoAdapters'
import { parseBoolean } from '../utils/preloadedState'
import { DetailsModal } from './DetailsModal'
import { InteractiveFloorplan } from './InteractiveFloorplan'
import InventoryList from './InventoryList'
import type { FloorPlanState } from './interactiveFloorplanReducer'
import type { EventData, VenueInfo, Inventory } from './types'
import type { InventoryCartState } from '../reducers/inventoryCartReducer'
import type { Map as ImmutableMap } from 'immutable'

const HEADER_HEIGHT = '70px'
const FOOTER_HEIGHT = '80px'
export const SIDEBAR_WIDTH = 330
export const FLOORPLAN_SVG_MAX_WIDTH = 1024

interface InteractiveFloorplanContainerProps {
  deviceSize: 'large' | 'small'
  dismiss: () => void
  eventData: EventData
  venueInfo: VenueInfo
  richText: string
  inventoryData: { [key: string]: Inventory }
  inventoryEdits: { [inventoryId: string]: { availabilityId: string } }
  soldInventory: Inventory[]
  boundToggleInventoryItem: (inventoryId: string) => void
  svg: string
  tieredEvents: string[]
  isPrivate: boolean
  isEventOpened: boolean
  date: string
  layoutHeight?: number
  pageBackgroundColor: string
  colorPrimary: string
  fontsColorButton: string
  fontsColorPrimary: string
  rgbaWidgetBackground: string
  userSelection: { date: string; eventId: string }
  svgInventory: FloorPlanState['svgInventory']
  eventDateFormatted: string
  eventTimeFormatted: string
  eventDescription: string
  eventImagesWithFallback: {
    src: string
    fallback: string
    crop: {
      height: number
      width: number
      x: number
      y: number
    }
  }[]
  isMultiInventoryEnabled: boolean
  inventoryWithQuantity: InventoryCartState['cart']
  isContinueButtonDisabled: boolean
  continueToCheckout: (inventoryItems: { inventoryId: string; availabilityId: string }[]) => void
  totalPrice: number
  currencyFormatter: Intl.NumberFormat
}

function InteractiveFloorplanContainer({
  deviceSize,
  dismiss,
  eventData,
  venueInfo,
  richText,
  inventoryData,
  inventoryEdits,
  layoutHeight,
  soldInventory,
  boundToggleInventoryItem,
  svg,
  tieredEvents,
  isPrivate,
  isEventOpened,
  date,
  pageBackgroundColor,
  colorPrimary,
  fontsColorButton,
  fontsColorPrimary,
  rgbaWidgetBackground,
  userSelection,
  svgInventory,
  eventDateFormatted,
  eventTimeFormatted,
  eventDescription,
  eventImagesWithFallback,
  isMultiInventoryEnabled,
  inventoryWithQuantity,
  isContinueButtonDisabled,
  continueToCheckout,
  totalPrice,
  currencyFormatter,
}: InteractiveFloorplanContainerProps) {
  const svgRef = useRef<HTMLDivElement>(null)
  const panzoom = useRef<ReturnType<typeof Panzoom>>()

  const [shouldShowEventDetailsModal, setShouldShowEventDetailsModal] = useState(false)

  const isDesktopLayout = deviceSize === 'large'
  const backgroundColorKey = 'backgroundColor' as ThemeColor
  const textColorKey = 'textColor' as ThemeColor

  useEffect(() => {
    const templateStylesElem = isDesktopLayout && document.getElementById('templateStyles')
    const parentTable = document.getElementById('bw-table')

    if (templateStylesElem) {
      document.head.removeChild(templateStylesElem)
      if (parentTable) {
        parentTable.style.width = '100vw'
        parentTable.style.height = '100vh'
        parentTable.style.backgroundColor = pageBackgroundColor
      }
    }
    return () => {
      if (templateStylesElem) {
        if (parentTable) {
          parentTable.style.width = ''
          parentTable.style.height = ''
          parentTable.style.backgroundColor = ''
        }
        document.head.appendChild(templateStylesElem)
      }
    }
  }, [isDesktopLayout, pageBackgroundColor])

  useEffect(() => {
    const svgContainer = svgRef.current
    if (!isDesktopLayout && svgContainer) {
      panzoom.current = Panzoom(svgContainer, { minScale: 0.25, maxScale: 2 })
    }

    return () => {
      if (panzoom.current) {
        panzoom.current.destroy()
      }
    }
  }, [isDesktopLayout])

  const handleSvgLayerClick = (inventoryName: string, selectedDate: string) => {
    const element = svgInventory[selectedDate]?.find(inventory => inventory.inventoryName === inventoryName)?.elements[0]
    if (element) {
      if (isDesktopLayout) {
        scrollIntoView(element, { block: 'center', inline: 'center', behavior: 'smooth' })
        return
      }

      panzoom.current?.zoom(1)
      setTimeout(() => {
        if (!svgRef.current) {
          return
        }

        const inventoryLayerBoundingRect = element.getBoundingClientRect()
        const svgBoundingRect = svgRef.current.getBoundingClientRect()
        const parentBoundingRect = svgRef.current.parentElement?.getBoundingClientRect()
        const horizontalCenter =
          inventoryLayerBoundingRect.left -
          svgBoundingRect.left -
          (parentBoundingRect?.width || 0) / 2 +
          inventoryLayerBoundingRect.width / 2
        const verticalCenter =
          inventoryLayerBoundingRect.top -
          svgBoundingRect.top -
          (parentBoundingRect?.height || 0) / 2 +
          inventoryLayerBoundingRect.height / 2
        panzoom.current?.pan(-horizontalCenter, -verticalCenter, { animate: true })
      })
    }
  }

  const handleSvgInit = (element: Element) => {
    if (isDesktopLayout) {
      scrollIntoView(element, { block: 'center', inline: 'center', behavior: 'smooth' })
      return
    }

    setTimeout(() => {
      const svgElement = svgRef.current?.getElementsByTagName('svg')[0]

      if (!svgElement || !svgRef.current || !svgRef.current.parentElement) {
        return
      }

      const svgBoundingRect = svgElement.getBoundingClientRect()
      const parentBoundingRect = svgRef.current.parentElement.getBoundingClientRect()
      const widthRatio = parentBoundingRect.width / svgBoundingRect.width
      const heightRatio = parentBoundingRect.height / svgBoundingRect.height

      const ratioToApply = widthRatio < heightRatio ? widthRatio : heightRatio
      panzoom.current?.zoom(ratioToApply)

      setTimeout(() => {
        if (!svgRef.current || !svgRef.current.parentElement) {
          return
        }

        const svgBoundingRect = svgElement.getBoundingClientRect()
        const parentBoundingRect = svgRef.current.parentElement.getBoundingClientRect()
        const x =
          (parentBoundingRect.left - svgBoundingRect.left + (parentBoundingRect.width / 2 - svgBoundingRect.width / 2)) / ratioToApply
        const y =
          (parentBoundingRect.top - svgBoundingRect.top + (parentBoundingRect.height / 2 - svgBoundingRect.height / 2)) / ratioToApply
        if (x !== undefined && y !== undefined) {
          panzoom.current?.pan(x, y)
        }
      }, 50)
    }, 50)
  }

  const header = (
    <>
      <HStack alignItems="center">
        <IconButton size="s" borderType="round" icon="GX-back" onClick={dismiss} iconColor="darkFont" iconBackgroundColor="lightFont" />
      </HStack>
      <HStack flex="1 1 auto" alignItems="center" justifyContent="center">
        <div>
          <StyledDateTimeWrapper textAlign="center">
            <Text fontSize="xl" color={textColorKey}>
              {eventDateFormatted}
            </Text>
          </StyledDateTimeWrapper>
          <StyledDateTimeWrapper textAlign="center">
            {eventTimeFormatted && (
              <Text fontSize="xs" color={textColorKey}>
                {eventTimeFormatted}
              </Text>
            )}
          </StyledDateTimeWrapper>
        </div>
      </HStack>
      <HStack alignItems="center">
        <StyledInfoButton
          aria-label="Show Additional Information"
          type="button"
          onClick={() => {
            setShouldShowEventDetailsModal(true)
          }}
        >
          <Icon size="2x" name="GX-info-outline" color={textColorKey} />
        </StyledInfoButton>
      </HStack>
      <Box />
    </>
  )

  const desktopLayoutHeight = isMultiInventoryEnabled && layoutHeight ? layoutHeight + parseInt(FOOTER_HEIGHT) : undefined
  const desktopLayout = (
    <StyledCenteredVerticallyOnScreen width="100vw">
      <Flex justifyContent="center" height={desktopLayoutHeight}>
        <div>
          <Flex height={layoutHeight}>
            <Flex
              display="block"
              flex={`0 0 ${SIDEBAR_WIDTH}px`}
              overflowY="auto"
              borderRightColor="borders"
              backgroundColor={backgroundColorKey}
            >
              <HStack
                backgroundColor={backgroundColorKey}
                borderBottomColor="borders"
                p="s"
                position="sticky"
                top="none"
                zIndex={1}
                height={HEADER_HEIGHT}
              >
                {header}
              </HStack>
              <InventoryList
                inventoryData={inventoryData}
                inventoryEdits={inventoryEdits}
                isPrivate={isPrivate}
                isEventOpened={isEventOpened}
                date={date}
                tieredEvents={tieredEvents}
                onSvgLayerClick={handleSvgLayerClick}
              />
              <div style={{ backgroundColor: rgbaWidgetBackground }}>
                <EventDetails
                  venueInfo={venueInfo}
                  richText={richText}
                  windowSize={0} // show details vertically
                />
              </div>
            </Flex>
            <StyledFlexWithNoScrollbar
              display="block"
              flex="0 1 auto"
              minWidth="200px"
              maxWidth={`${FLOORPLAN_SVG_MAX_WIDTH}px`}
              overflow="auto"
              backgroundColor={backgroundColorKey}
            >
              <InteractiveFloorplan
                availableInventory={Object.values(inventoryData)}
                soldInventory={soldInventory}
                boundToggleInventoryItem={boundToggleInventoryItem}
                selectedDate={userSelection.date}
                svgRef={svgRef}
                svg={svg}
                svgInventory={svgInventory}
                tieredEvents={tieredEvents}
                onSvgInit={handleSvgInit}
              />
            </StyledFlexWithNoScrollbar>
          </Flex>
          {isMultiInventoryEnabled && (
            <StyledFooter>
              <StyledTotalPrice>Total: {currencyFormatter.format(totalPrice)}</StyledTotalPrice>
              <div>
                <ContinueButton
                  aria-label="continue to checkout"
                  disabled={isContinueButtonDisabled}
                  onClick={() => {
                    const inventories = inventoryWithQuantity.map(inventoryCartItem => ({
                      inventoryId: inventoryCartItem.inventoryId,
                      availabilityId: inventoryEdits[inventoryCartItem.inventoryId]?.availabilityId || '',
                    }))
                    continueToCheckout(inventories)
                  }}
                >
                  Continue
                </ContinueButton>
              </div>
            </StyledFooter>
          )}
        </div>
      </Flex>
    </StyledCenteredVerticallyOnScreen>
  )

  const mobileLayoutFloorplanHeight = isMultiInventoryEnabled
    ? `calc(50vh - ${HEADER_HEIGHT} - ${FOOTER_HEIGHT})`
    : `calc(50vh - ${HEADER_HEIGHT})`
  const mobileLayout = (
    <Box width="100vw" position="absolute" left="none">
      <Box backgroundColor={backgroundColorKey}>
        <HStack p="s" borderBottomColor="borders" height={HEADER_HEIGHT}>
          {header}
        </HStack>
        <Box height={mobileLayoutFloorplanHeight}>
          <InteractiveFloorplan
            availableInventory={Object.values(inventoryData)}
            soldInventory={soldInventory}
            boundToggleInventoryItem={boundToggleInventoryItem}
            selectedDate={userSelection.date}
            svgRef={svgRef}
            svg={svg}
            svgInventory={svgInventory}
            tieredEvents={tieredEvents}
            onSvgInit={handleSvgInit}
          />
        </Box>
        <Box height="50vh" overflowY="auto" borderTopColor="borders">
          <InventoryList
            inventoryData={inventoryData}
            inventoryEdits={inventoryEdits}
            isPrivate={isPrivate}
            isEventOpened={isEventOpened}
            date={date}
            tieredEvents={tieredEvents}
            onSvgLayerClick={handleSvgLayerClick}
          />
          <div style={{ backgroundColor: rgbaWidgetBackground }}>
            <EventDetails
              venueInfo={venueInfo}
              richText={richText}
              windowSize={0} // show details vertically
            />
          </div>
        </Box>
        {isMultiInventoryEnabled && (
          <StyledFooter>
            <StyledTotalPrice>Total: {currencyFormatter.format(totalPrice)}</StyledTotalPrice>
            <div>
              <ContinueButton
                aria-label="continue to checkout"
                disabled={isContinueButtonDisabled}
                onClick={() => {
                  const inventories = inventoryWithQuantity.map(inventoryCartItem => ({
                    inventoryId: inventoryCartItem.inventoryId,
                    availabilityId: inventoryEdits[inventoryCartItem.inventoryId]?.availabilityId || '',
                  }))
                  continueToCheckout(inventories)
                }}
              >
                Continue
              </ContinueButton>
            </div>
          </StyledFooter>
        )}
      </Box>
    </Box>
  )

  const themeOverride = {
    colors: {
      ...gxTheme.colors,
      backgroundColor: rgbaWidgetBackground,
      textColor: fontsColorPrimary,
      primaryBackground: rgbaWidgetBackground,
      primaryFont: fontsColorPrimary,
    },
    button: {
      primary: {
        backgroundColor: colorPrimary,
        textColor: fontsColorButton,
      },
    },
  }

  return (
    <Root theme="gx" themeOverride={themeOverride}>
      {isDesktopLayout ? desktopLayout : mobileLayout}
      {shouldShowEventDetailsModal && (
        <DetailsModal
          title={eventData.name}
          modalAriaLabel={`Event Details for ${eventData.name}`}
          imagesWithFallback={eventImagesWithFallback}
          onClose={() => {
            setShouldShowEventDetailsModal(false)
          }}
        >
          <>
            <HStack spacing="s" mt="m" flexWrap="wrap">
              <div>{eventDateFormatted}</div>
              {eventTimeFormatted && (
                <>
                  <div>•</div>
                  <div>{eventTimeFormatted}</div>
                </>
              )}
            </HStack>
            {eventDescription && (
              <>
                <DividerLine margin="none" mt="m" mb="m" />
                <div>
                  <Text textStyle="h3">About</Text>
                </div>
                <Box mt="m" mb="lm">
                  <HTMLContent
                    content={eventDescription}
                    allowedTags={['div', 'span', 'br', 'u', 'b', 'strong', 'i', 'em', 'a', 'ol', 'ul', 'li']}
                  />
                </Box>
              </>
            )}
          </>
        </DetailsModal>
      )}
    </Root>
  )
}

const mapStateToProps = (state: {
  app: {
    language: string
  }
  resize: { height?: number }
  interactiveFloorplan: FloorPlanState
  widgetSettings: {
    baseUrl: string
    colorBackground?: string
    colorBackgroundOpacity?: string
    colorPrimary: string
    fontsColorButton: string
    fontsColorPrimary: string
    colorWidgetBackground: string
    colorWidgetBackgroundOpacity: string
    isFeesInPriceDisplayed: string
  }
  venueInfo: VenueInfo
  userSelection: ImmutableMap<string, string>
  entities: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    events: ImmutableMap<string, ImmutableMap<string, any>>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inventory: ImmutableMap<string, ImmutableMap<string, any>>
  }
  inventoryCart: InventoryCartState
}) => {
  const selectedDate = state.userSelection.get('date')
  const selectedEventId = state.userSelection.get('eventId')
  const event = state.entities.events.get(selectedEventId)
  const eventStartTime = event?.get('eventStartTime')
  const eventEndTime = event?.get('eventEndTime')
  const eventDescription = event?.get('description') || ''
  const eventPhotoMaps: RawPhotoData[] = Object.values(event?.get('photoMap')?.toJS() || {})
  const eventImagesWithFallback = eventPhotoMaps
    .filter((photo, index) => index > 0 && !!photo.url)
    .map(photo => {
      const photoData = adaptPhotoData(photo, 'large', state.widgetSettings.baseUrl)
      return {
        src: photoData.src,
        fallback: photoData.srcFallback,
        crop: photoData.cropData,
      }
    })

  const floorplanHeight = state.interactiveFloorplan.height
  const windowHeight = state.resize.height
  let layoutHeight
  if (floorplanHeight && windowHeight && floorplanHeight < windowHeight) {
    layoutHeight = floorplanHeight
  } else {
    layoutHeight = windowHeight
  }

  let pageBackgroundColor = ''
  const pageBackgroundColorSetting = state.widgetSettings.colorBackground
  const pageBackgroundColorOpacitySetting = state.widgetSettings.colorBackgroundOpacity
  if (pageBackgroundColorSetting && pageBackgroundColorOpacitySetting) {
    const rgba = hexToRgb(pageBackgroundColorSetting, pageBackgroundColorOpacitySetting)
    pageBackgroundColor = rgba ?? ''
  }

  const inventoryWithQuantity = state.inventoryCart.cart.filter(({ quantity }) => quantity > 0)
  const inventoryData: { [inventoryId: string]: { inventoryId: string; price: number } } = state.entities.inventory.toJS()
  const isFeesInPriceDisplayed = parseBoolean(state.widgetSettings.isFeesInPriceDisplayed)
  const totalPrice = inventoryWithQuantity.reduce((total, inventoryCartItem) => {
    const reservationTypeInventory = inventoryData[inventoryCartItem.inventoryId]
    if (!reservationTypeInventory) {
      return total
    }
    const totalFeePrice = calcTotalFeePrice(
      reservationTypeInventory,
      1,
      reservationTypeInventory.price,
      state.venueInfo.venueDefaultServiceCharge
    )
    const totalPrice = isFeesInPriceDisplayed ? reservationTypeInventory.price + totalFeePrice : reservationTypeInventory.price
    return total + inventoryCartItem.quantity * totalPrice
  }, 0)

  const { colorPrimary, fontsColorButton, fontsColorPrimary, colorWidgetBackground, colorWidgetBackgroundOpacity } = state.widgetSettings

  return {
    venueInfo: state.venueInfo,
    layoutHeight,
    pageBackgroundColor,
    colorPrimary,
    fontsColorButton,
    fontsColorPrimary,
    rgbaWidgetBackground: hexToRgb(colorWidgetBackground, colorWidgetBackgroundOpacity) ?? '',
    userSelection: state.userSelection.toJS(),
    svgInventory: state.interactiveFloorplan.svgInventory,
    eventDateFormatted: DateTime.from(selectedDate).toDateOnly().formatNYearSMonthNDay(),
    eventTimeFormatted:
      eventStartTime && eventEndTime ? `${TimeOnly.from(eventStartTime).formatSTime()} - ${TimeOnly.from(eventEndTime).formatSTime()}` : '',
    eventDescription,
    eventImagesWithFallback,
    isMultiInventoryEnabled: isMultiInventoryEnabled(state),
    inventoryWithQuantity,
    isContinueButtonDisabled: inventoryWithQuantity.length === 0,
    totalPrice,
    currencyFormatter: new Intl.NumberFormat(state.app.language, { style: 'currency', currency: state.venueInfo.currencyCode }),
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
  continueToCheckout: (inventoryItems: { inventoryId: string; availabilityId: string }[]) => {
    dispatch(selectInventoryItems(inventoryItems))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(InteractiveFloorplanContainer)

const {
  fonts_color_primary: fontsColorPrimary,
  color_cell_background: colorCellBackground,
  color_cell_background_opacity: colorCellBackgroundOpacity,
  color_lines: colorLines,
  color_lines_opacity: colorLinesOpacity,
} = window.widgetInit.settings

const StyledInfoButton = styled(BasicButton)`
  width: 42px;
  height: 42px;
`

const StyledFlexWithNoScrollbar = styled(Flex)`
  ::-webkit-scrollbar {
    display: none;
  }
`

const StyledCenteredVerticallyOnScreen = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`

// Reset the font-size set by #bw-wrapper
const StyledDateTimeWrapper = styled(Box)`
  font-size: initial;
`

const StyledFooter = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  height: ${FOOTER_HEIGHT};
  background-color: ${hexToRgb(colorCellBackground, colorCellBackgroundOpacity)};
  padding: 0 16px;
  border-top: 1px solid ${hexToRgb(colorLines, colorLinesOpacity)};
`

const StyledTotalPrice = styled.div`
  font-size: 18px;
  color: ${fontsColorPrimary};
  font-weight: 500;
`
